import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Send, X, CheckCircle } from 'lucide-react';
import { apiClient } from '../api/apiClient';
import { errorMessage } from '../api/errorMessage';
import './FeedbackModal.css';

const PAGE_MAP = {
  '/collection': 'Sammlung',
  '/decks': 'Decks',
  '/friends': 'Freunde',
  '/wantlist': 'Wantlist',
  '/admin': 'Anderes',
  '/admin/patch-notes': 'Anderes',
};

const PAGES_LIST = [
  'Sammlung',
  'Decks',
  'Deck Editor',
  'Wantlist',
  'Freunde',
  'Playtest',
  'Anderes',
];

function getPageLabel(pathname) {
  if (PAGE_MAP[pathname]) return PAGE_MAP[pathname];
  if (pathname.match(/^\/decks\/[^/]+\/editor/)) return 'Deck Editor';
  if (pathname.startsWith('/wantlist')) return 'Wantlist';
  if (pathname.startsWith('/playtest')) return 'Playtest';
  if (pathname.startsWith('/admin')) return 'Anderes';
  if (pathname.startsWith('/decks')) return 'Decks';
  return 'Anderes';
}

const FEEDBACK_TYPES = [
  { value: 'feedback', label: 'Feedback' },
  { value: 'bug', label: 'Bug gefunden' },
  { value: 'feature', label: 'Feature-Wunsch' },
];

export default function FeedbackModal({ open, onClose }) {
  const location = useLocation();

  const [type, setType] = useState('feedback');
  const [page, setPage] = useState(() => getPageLabel(location.pathname));
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const submitMutation = useMutation({
    mutationFn: (data) => apiClient.post('/api/feedback', data),
    meta: { handlesError: true },
    onSuccess: () => setSuccess(true),
  });
  const { reset: resetSubmitMutation } = submitMutation;

  // Reset draft when switching routes; keep it when only closing/reopening on same page.
  useEffect(() => {
    setType('feedback');
    setPage(getPageLabel(location.pathname));
    setMessage('');
    setSuccess(false);
  }, [location.pathname]);

  // Erfolgs- und Fehlerzustand gehören zum Sendevorgang, nicht zum Entwurf, und
  // werden beim Öffnen zurückgesetzt: Das Modal hängt im Benutzermenü und bleibt
  // über das Schließen hinaus gemountet — ohne diesen Reset zeigt es nach einem
  // gesendeten Feedback dauerhaft den Danke-Bildschirm statt des Formulars.
  useEffect(() => {
    if (!open) return;
    setSuccess(false);
    resetSubmitMutation();
  }, [open, resetSubmitMutation]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Escape key to close
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, handleClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    submitMutation.mutate({ type, page, message: message.trim() });
  };

  const isValid = message.trim().length >= 10;

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay" onClick={handleClose} />
      <div className="modal-content feedback-modal" onClick={(e) => e.stopPropagation()}>
        {success ? (
          <div className="feedback-success">
            <CheckCircle size={48} className="feedback-success-icon" />
            <h2>Danke für dein Feedback!</h2>
            <p>Wir haben deine Nachricht erhalten und werden sie prüfen.</p>
            <div className="modal-actions">
              <button onClick={handleClose}>Schließen</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="feedback-header">
              <h2>Feedback senden</h2>
              <button type="button" className="feedback-close-btn" onClick={handleClose} aria-label="Schließen">
                <X size={18} />
              </button>
            </div>

            <p className="feedback-intro">
              Hi, wir arbeiten noch aktiv an dieser Seite. Daher ist dein Feedback super wichtig
              für uns. Falls dir etwas auffällt oder du etwas vermisst, lass es uns wissen.
            </p>

            {submitMutation.isError && (
              <div className="feedback-error">
                {errorMessage(submitMutation.error, 'Fehler beim Senden. Bitte versuche es erneut.')}
              </div>
            )}

            <div className="feedback-field">
              <label>Typ</label>
              <div className="feedback-type-selector">
                {FEEDBACK_TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    className={`feedback-type-btn${type === t.value ? ' active' : ''}`}
                    onClick={() => setType(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="feedback-field">
              <label htmlFor="feedback-page">Seite</label>
              <select
                id="feedback-page"
                value={page}
                onChange={(e) => setPage(e.target.value)}
              >
                {PAGES_LIST.map((p) => (
                  <option key={p} value={p}>{p === 'Anderes' ? 'Anderes...' : p}</option>
                ))}
              </select>
            </div>

            <div className="feedback-field">
              <label htmlFor="feedback-message">Nachricht</label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  type === 'bug'
                    ? 'Beschreibe den Fehler: Was hast du gemacht? Was ist passiert? Was hast du erwartet?'
                    : type === 'feature'
                      ? 'Beschreibe das Feature, das du dir wünschst...'
                      : 'Deine Nachricht...'
                }
                rows={5}
                maxLength={5000}
                required
                minLength={10}
              />
              <span className="feedback-charcount">{message.length} / 5000</span>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={handleClose}>
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={!isValid || submitMutation.isPending}
              >
                <Send size={16} />
                {submitMutation.isPending ? 'Wird gesendet...' : 'Absenden'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
